/* eslint-disable */
import {
  CommentTarget,
  Constants,
  ErrorUtilities,
  TextUtilities,
} from '@pwc/pwc-deals-web-common';
import {
  Button,
  Tooltip,
  PopoverMenu,
  Icon,
  PageNotViewable,
} from '@pwc/pwc-deals-ui';
import { USER_ROLES } from '@pwc/pwc-deals-web-common';
import { getUserRole } from 'common/redux/security/loggedInUser/loggedInUserSlice';
import { Formik } from 'formik';
import { debounce, isEmpty } from 'lodash';
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Link,
  Route,
  Switch,
  useHistory,
  useParams,
  useRouteMatch,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import D3Alert from 'common/components/Alert/D3Alert';
import { CKEditorContext } from 'common/contexts/CKEditorContext.tsx';

import ActiveContentHeader from 'common/components/ContentHeaders/ActiveContentHeader';
import { FormLayout } from 'common/components/layouts';
import { LinkedContentNavigation } from 'common/components/LinkedContentNavigation/LinkedContentNavigation';
import PublishButton from 'common/components/PublishButton';
import RichTextBalloonEditorFormikField from 'common/components/RichTextBalloonEditorFormikField/RichTextBalloonEditorFormikField';
import Separator from 'common/components/Separator';
import insertChartAndTable from 'common/helpers/insertMinitableEventListener';
import { getErrorMessageDuplicateTitle } from 'common/helpers/duplicateTitleError';
import {
  getProject,
  getProjectId,
  getProjectName,
} from 'features/project/projectSlice';

import {
  restrictedErrorMessage,
  isTitleRestricted,
} from 'common/helpers/titlesBlackList';
import useIsMountedRef from 'common/hooks/useIsMountedRef';
import useAllConnections from 'features/narratives/hooks/useAllConnections';
import { contentIsLinked } from 'features/narratives/narrativesUtilities';
import ConnectedContentContext from 'features/narratives/Context/ConnectedContentContext';
import EditConnectedContentNew from 'features/narratives/EditConnectedContentNew/EditConnectedContentNew';
import TitleFieldsetWithWordCount from 'common/components/TitleFieldWithWordCount/TitleFieldWithWordCount';
import VersionToggleContainer from 'common/components/VersionToggle/VersionToggleContainer';
import TagEditor from 'features/tagging/common/TagEditor/TagEditor';
import useMobile from 'common/hooks/useMobile';
import { useSyncedInfo } from 'common/components/Autosync/SyncedInfoProvider.tsx';
import { EmptyClipboardModal } from 'common/components/EmptyClipboardModal';
import featureFlags, {
  isReportBuilderLivePreviewEnabled,
  isLiveReportPreviewEnabled,
} from 'common/helpers/featureFlags';
import { getTandCterritory } from '../../../../common/helpers/projectInitUtil';
import useTabletOrMobile from '../../../../common/hooks/useTabletOrMobile';
import { showModalError } from '../../../../common/redux/errors/errorsSlice';
import {
  analysesGroupsListPath,
  analysesListPath,
} from '../../../../common/services/urlService';

import {
  getLibraryItemsSelector,
  getReportPreviewPropsSelector,
  setReportPreviewProps,
} from '../../libraryItemsSlice';
import getLibraryItems from '../../../liveReportPreview/utils';

import FreeformAnalysisLayoutContainer from '../FreeformAnalysisLayout/FreeformAnalysisLayoutContainer';
import LibraryItemListContainer from '../LibraryItemListContainer/LibraryItemListContainer';
import './FreeformAnalysisForm.scss';
import {
  RTEContextProvider,
  useSectionsState,
} from './FreeformAnalysisFormContext';
import {
  mapDto,
  validateTitle,
} from './FreeformAnalysisFormUtil/FreeformAnalysisFormUtil';
import { deleteAnalysis, projectsContentsSearch } from '../../analysesService';
import useDeviceViewport from '../../../../common/hooks/useDeviceViewport';
import { deviceViewportNames } from '../../../../common/constants/DeviceViewports';
import updateEntityVersion from '../../../../common/actions/updateEntityVersion';
import { getEntityVersion } from '../../../comments/commentsSlice';
import AutoSave from '../../../../common/components/AutoSave';
import { useEventHub, useEventSubscription } from '@pwc/event-hub-ui';
import FormActions from '../../../../common/components/FormActions';
import {
  AUTO_SAVE_IDLE_ERROR,
  AUTO_SAVE_LOCK_ERROR,
} from '../../../../common/components/GlobalError/constants';
import { useLiveReportPreview } from '@pwc/pwc-deals-report-ui/build/components/ReportPreview/provider';
import { getConvertedAnalysis } from './reportPreviewSupport';
import { useDirtyCheck } from '@pwc/auto-save-ui';
import {
  selectAnalysisVersions,
  selectedVersionStatus,
  updateSelectionVersion,
} from '../../analysisSlice';
import { viewAnalysisPath } from '../../../../common/services/urlService';
import { ContextRoot } from '../../../../common/contexts/Root';

/**
 * Component usage: Freeform analysis in edit mode
 */
const FreeformAnalysisForm = ({
  analysisTitle,
  analysisInsight,
  analysisBody,
  analysis,
  publishAction,
  previewAction,
  cancelAction,
  appliedTags,
  onSaveText,
  standbyText,
  parentContentId,
  sections,
  draftVersion,
  sharedVersion,
  commentCounts,
  isCreateForm,
  isAutoSaveCalled,
  setConnectedContentChanged,
  handleRedirectToView,
}) => {
  const { bootStrap } = useContext(ContextRoot);

  const isPwcUser = useSelector(getUserRole) === USER_ROLES.PWC_USER;
  const project = useSelector(getProject);
  const refInitialLivePreviewDataIsSet = useRef(false);
  const [isFormClean, setIsFormClean] = useState(true);
  const refIsIngesting = useRef(false);
  const isMountedRef = useIsMountedRef();
  const formRef = useRef();
  const [errorType, setErrorType] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingFulfilled, setIsSavingFulfilled] = useState(false);
  const [isSavingRejected, setIsSavingRejected] = useState(false);
  const [tags, setTags] = useState(appliedTags || []);
  const [title, setTitle] = useState(analysisTitle);
  const [insight, setInsight] = useState(analysisInsight);
  const [body, setBody] = useState(analysisBody);
  const { path, url } = useRouteMatch();
  const { currentSections } = useSectionsState();
  const [deactivateCallback, setDeactivateCallback] = useState(null);
  const [narratives, setNarratives] = useState(null);
  const [titleError, setTitleError] = useState(null);
  const { projectId, analysisId } = useParams();
  const [, setLayoutSaved] = useState(false);
  const [noDataOnClipboard, setNoDataOnClipboard] = useState(false);
  const darkMode = useSelector((state) => state.userSettings.settings.darkMode);
  const entityVersion = useSelector(getEntityVersion);
  const projectName = useSelector(getProjectName);
  const contextStatusInfoItem = useSyncedInfo();
  const history = useHistory();
  const [initialForm, setInitialForm] = useState();
  const dirtyCheck = useDirtyCheck(initialForm);
  const selectedVersions = useSelector(selectAnalysisVersions);
  const selectedVersion = useSelector(selectedVersionStatus);
  const { eventHub } = useEventHub();
  const { t } = useTranslation();

  const handleDelete = async (callback) => {
    try {
      await deleteAnalysis(projectId, analysis.contentThreadId);
      callback();
      history.push(analysesGroupsListPath(projectId));
    } catch (e) {
      callback();
      dispatch(
        showModalError(Constants.MoreMenuErrorType.UNABLE_TO_SAVE_CHANGE)
      );
    }
  };

  const dispatch = useDispatch();
  const { allConnections: allRetrievedConnections, error } = useAllConnections(
    isMountedRef.current
  );
  const [connectedLinkedContent, setConnectedLinkedContent] = useState({
    analyses: [],
    hypotheses: [],
  });
  const [connectedLinkedContentLoaded, setConnectedLinkedContentLoaded] =
    useState(false);

  const isMobile = useMobile();
  const currentDeviceViewport = useDeviceViewport();

  useEffect(() => {
    if (featureFlags.isVersioningPhase1Enabled) {
      if (!selectedVersion) {
        dispatch(
          updateSelectionVersion({
            contentId: draftVersion?.contentId || sharedVersion?.contentId,
          })
        );
      }

      if (selectedVersions?.some(({ isSelected }) => isSelected)) {
        history.replace(viewAnalysisPath(projectId, analysisId));
      }
    }
  }, [
    analysisId,
    draftVersion,
    history,
    projectId,
    selectedVersion,
    selectedVersions,
    sharedVersion,
  ]);

  useEffect(() => {
    if (!narratives && allRetrievedConnections) {
      const findThisAnalysis = Object.keys(allRetrievedConnections).map(
        (key) => {
          const connectedAnalyses = allRetrievedConnections[key].filter(
            (obj) => {
              if (obj.contentId !== analysis.contentId) {
                return obj;
              }

              return connectedAnalyses;
            }
          );

          return { [key]: connectedAnalyses };
        }
      );

      const objectConnections = Object.assign({}, ...findThisAnalysis);

      setNarratives(objectConnections);
    }
  }, [allRetrievedConnections, narratives, analysis.contentId]);

  const { lastSelectedKFA } = useContext(ConnectedContentContext);

  useMemo(() => {
    if (narratives) {
      const linkedAllConnections = Object.keys(narratives).map((key) => {
        const connectedAnalyses = narratives[key].filter((obj) => {
          const isObj = contentIsLinked(obj.links, parentContentId);

          return isObj;
        });

        return { [key]: connectedAnalyses };
      });

      const objectConnections = Object.assign({}, ...linkedAllConnections);
      setConnectedLinkedContent(objectConnections);
      setConnectedLinkedContentLoaded(true);

      return [];
    }

    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    narratives,
    parentContentId,
    setConnectedContentChanged,
    lastSelectedKFA,
  ]);

  const titleField = {
    name: 'title',
    value: title,
  };
  const insightField = {
    name: 'insight',
    value: insight,
  };

  const bodyField = {
    name: 'body',
    value: body,
  };

  const isOnlySharedAvailable = sharedVersion && isEmpty(draftVersion);

  const removeLinkedObjects = (currentSections) => {
    let modifiedSection = currentSections.map((elementa) => {
      let modifiedItem = elementa.items.map((elementb) => {
        let parsedObject = {};
        if (elementb.data) {
          parsedObject = { ...JSON.parse(elementb.data) };
          if (parsedObject.LinkedObjects) {
            delete parsedObject.LinkedObjects;
          }
        }
        return {
          ...elementb,
          data: JSON.stringify(parsedObject),
        };
      });
      return {
        ...elementa,
        items: modifiedItem,
      };
    });
    return modifiedSection;
  };

  const handlePublish = async () => {
    if (refIsIngesting.current || isSaving) {
      return;
    }

    const isUniqueTitle = await analysisSearchTitle(title);

    if (!isUniqueTitle) {
      return;
    }

    setIsSaving(true);
    setIsSavingRejected(false);

    try {
      setErrorType();
      const { contentThreadId = null } = analysis;

      await publishAction(
        mapDto({
          body,
          contentThreadId,
          insight,
          sections: removeLinkedObjects(currentSections),
          tags,
          title,
        }),
        narratives,
        lastSelectedKFA
      );
      setIsSavingFulfilled(true);
      eventHub?.publish('comment-reload-comment-threads', {});
    } catch (e) {
      setIsSavingFulfilled(false);
      setIsSavingRejected(true);
      setErrorType(e);
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    async function autoSave() {
      await handlePublish();
    }
    if (
      isOnlySharedAvailable &&
      formRef.current &&
      !isSaving &&
      !isAutoSaveCalled &&
      connectedLinkedContentLoaded
    ) {
      autoSave();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formRef,
    isSaving,
    isOnlySharedAvailable,
    isAutoSaveCalled,
    connectedLinkedContentLoaded,
  ]);

  useEffect(() => {
    const { versionNumber } = draftVersion || {};

    if (versionNumber && versionNumber !== entityVersion) {
      dispatch(updateEntityVersion(versionNumber));
    }
  }, [draftVersion]);

  const handlePreview = async () => {
    const isUniqueTitle = await analysisSearchTitle(title);

    if (!isUniqueTitle) {
      return;
    }

    setIsSaving(true);

    try {
      setErrorType();
      const { contentThreadId = null } = analysis;

      await previewAction(
        mapDto({
          body,
          contentThreadId,
          insight,
          sections: currentSections,
          tags,
          title,
        }),
        narratives,
        lastSelectedKFA
      );
    } catch (e) {
      setErrorType(ErrorUtilities.getContentErrorType(e));
    } finally {
      setIsSaving(false);
    }
  };

  const handleTagSelect = (values) => {
    setTags(values);
    if (isCreateForm && isFormClean) {
      setIsFormClean(false);
    }
  };

  const setEventListener = useCallback(
    (editor) => {
      const editorContainer = document.getElementById('rich-text-editor');
      if (deactivateCallback) {
        deactivateCallback();
        setDeactivateCallback(null);
      }

      if (editorContainer && editor) {
        const removeListener = insertChartAndTable({
          bootStrap,
          contextStatusInfoItem,
          darkMode,
          dispatch,
          editor,
          isPwcUser,
          editorContainer,
          onChangeState({ type }) {
            switch (type) {
              case 'start':
                refIsIngesting.current = true;
              case 'success':
                refIsIngesting.current = false;
                handlePublish();
              case 'end':
                refIsIngesting.current = false;

              default:
            }
          },
          projectId,
          setNoDataOnClipboard,
        });

        setDeactivateCallback(() => {
          return () => {
            removeListener();
          };
        });
      }
    },
    [deactivateCallback, projectId, darkMode, dispatch, isPwcUser]
  );

  const analysisSearchTitle = useCallback(
    async (searchTitle) => {
      if (!searchTitle.length) {
        setTitleError(null);
        return false;
      }
      if (isTitleRestricted(searchTitle)) {
        setTitleError(restrictedErrorMessage);
        return false;
      }
      const trimmedTitle = TextUtilities.trimText(searchTitle);
      const err = validateTitle(trimmedTitle);
      if (err.length > 0) {
        setTitleError(err);
        return false;
      }
      try {
        const matchItems = await projectsContentsSearch(projectId, {
          title: trimmedTitle,
        });
        if (
          matchItems &&
          matchItems.length > 0 &&
          TextUtilities.trimText(matchItems[0].title).toLowerCase() ===
            trimmedTitle.toLowerCase() &&
          matchItems[0].contentThreadId !== analysis.contentThreadId
        ) {
          setTitleError(getErrorMessageDuplicateTitle(matchItems[0], t));
          return false;
        }
        setTitleError(null);
      } catch (e) {
        setTitleError(ErrorUtilities.getErrorMessage(e));
        return false;
      }
      return true;
    },
    [projectId, analysis.contentThreadId, t]
  );

  const debouncedSearchTitle = useMemo(
    () => debounce(analysisSearchTitle, 250),
    [analysisSearchTitle]
  );

  useEffect(() => {
    if (isCreateForm && isFormClean) {
      if (
        title !== analysisTitle ||
        insight !== analysisInsight ||
        body !== analysisBody
      ) {
        setIsFormClean(false);
      }
    }
  }, [
    title,
    insight,
    body,
    isCreateForm,
    isFormClean,
    analysisTitle,
    analysisInsight,
    analysisBody,
  ]);

  const cancelButton = (isBottom) => (
    <Button
      className={
        isBottom
          ? 'button-md button-link button-theme mx-2 mr-2'
          : 'button-link button-theme mx-2 mr-2'
      }
      onClick={cancelAction}
      data-testid={isBottom ? 'cancel-button-bottom' : 'cancel-button-top'}
      type="button"
      aria-label="Cancel"
    >
      Cancel
    </Button>
  );

  const updateAndPreviewButton = (isBottom) => (
    <Tooltip
      direction={isBottom ? 'top' : 'bottom'}
      tip="This shows a preview of the analysis in the report. New content will be saved and added to Junction so that a preview can be generated"
    >
      <Button
        className={
          isBottom
            ? 'button-md button-outline mx-2'
            : 'button-xs button-outline mx-2'
        }
        onClick={handlePreview}
        data-testid="save-and-preview"
        type="button"
        disabled={titleError || isSaving}
        aria-label="Save and preview"
      >
        Update and preview
      </Button>
    </Tooltip>
  );

  const updateAndPreviewMoreMenu = (
    <PopoverMenu
      placement="bottom-end"
      id="popover-moremenu"
      data-testid="save-and-preview-popover"
      className="mr-3"
      isButtonSecondary
    >
      <Button
        type="button"
        className="button-menu"
        onClick={handlePreview}
        data-testid="save-and-preview-popover-button"
        disabled={isSaving}
        aria-label="Update and preview"
      >
        <Icon name="report" width="21" className="outline icon-orange" />
        Update and preview
      </Button>
    </PopoverMenu>
  );

  const isTablet =
    currentDeviceViewport === deviceViewportNames.tablet ||
    currentDeviceViewport === deviceViewportNames.tabletL;

  const publishingButton = (isBottom) => (
    <PublishButton
      buttonSize={isBottom ? 'button-md' : 'button-xs'}
      isSaving={isSaving}
      isValid={!titleError && Boolean(title.length)}
      buttonPosition="top"
      onSaveText={onSaveText}
      standbyText={standbyText}
      className="mr-3 ml-2"
    />
  );

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const { libraryItems } = useSelector(getLibraryItemsSelector);

  const currentForm = {
    ...mapDto({
      body,
      contentThreadId: analysis?.contentThreadId,
      insight,
      sections: currentSections,
      tags,
      title,
    }),
    linkedHypotheses: connectedLinkedContent.hypotheses.map(
      (h) => h.contentThreadId
    ),
    linkedAnalyses: connectedLinkedContent.analyses.map(
      (a) => a.contentThreadId
    ),
    lastSelectedKFA,
    libraryItems,
  };

  const handleDone = async () => {
    if (dirtyCheck.dataIsDirty(currentForm)) {
      await handlePublish();
    }

    handleRedirectToView();
  };

  const activeContent = (isBottom) => {
    const titleHasValue = title && title?.trim().length !== 0;
    return (
      <FormActions
        projectName={projectName}
        itemTitle={title}
        isAutoSaveEnabled
        position={isBottom ? 'bottom' : 'top'}
        isSaving={isSaving}
        isSavingFulfilled={isSavingFulfilled}
        isSavingRejected={isSavingRejected}
        isCreateMode={isCreateForm}
        isReportDisabled={isSaving || !!titleError}
        isDoneDisabled={isSaving || !titleHasValue || !!titleError}
        isDeleteDisabled={isSaving || !titleHasValue || !!titleError}
        cancelAction={cancelAction}
        reportAction={handlePreview}
        doneAction={handleDone}
        deleteAction={handleDelete}
        isPreviewHidden
      />
    );
  };

  const headerComponent = (
    <div className="toggle-version-container-edit-mode">
      <VersionToggleContainer
        redirectUrl={`/projects/${projectId}/analyses`}
        mode="edit"
        isEntitySharedWithClient={false}
        projectId={projectId}
        config={{
          commentCounts,
          draftVersion,
          sharedVersion,
        }}
        hideTextVersion={featureFlags.isVersioningPhase1Enabled}
      />
      <ActiveContentHeader>{activeContent(false)}</ActiveContentHeader>
    </div>
  );

  const footerComponent = activeContent(true);

  const isFormDataValid = !titleError;
  const isAutoSaveReady = formRef.current && connectedLinkedContentLoaded;

  useEffect(() => {
    if (isAutoSaveReady && !initialForm) {
      const currentForm = {
        ...mapDto({
          body,
          contentThreadId: analysis?.contentThreadId,
          insight,
          sections: currentSections,
          tags,
          title,
        }),
        linkedHypotheses: connectedLinkedContent.hypotheses.map(
          (h) => h.contentThreadId
        ),
        linkedAnalyses: connectedLinkedContent.analyses.map(
          (a) => a.contentThreadId
        ),
        lastSelectedKFA,
      };
      setInitialForm(currentForm);
    }
  }, [isAutoSaveReady, initialForm]);

  useEventSubscription('autosave-save', (event) => {
    handlePublish();
    dirtyCheck.setLastData(currentForm);
  });

  useEventSubscription('autosave-idle', (event) => {
    handleRedirectToView(AUTO_SAVE_IDLE_ERROR);
  });

  const getPayload = () =>
    getConvertedAnalysis({
      title,
      pageId: '.form-content-container',
      analysis: {
        ...analysis,
        tags: null,
        linkedHypotheses: null,
        linkedAnalyses: null,
        lastSelectedKFA: null,
      },
      insightId: "[data-testid='text-area-insight']",
      descriptionId: "[data-testid='text-area-body']",
      sections: currentSections,
      libraryItems,
      territory: getTandCterritory(),
    });
  const liveReportPreview = useLiveReportPreview();
  liveReportPreview.getData = () => ({
    ...currentForm,
    tags: null,
    linkedHypotheses: null,
    linkedAnalyses: null,
    lastSelectedKFA: null,
  });
  liveReportPreview.getPayload = getPayload;
  liveReportPreview.getProjectId = getProjectId;
  liveReportPreview.getReportPreviewPropsSelector =
    getReportPreviewPropsSelector;
  liveReportPreview.setReportPreviewProps = setReportPreviewProps;
  liveReportPreview.useTabletOrMobile = useTabletOrMobile;

  useEffect(() => {
    if (
      refInitialLivePreviewDataIsSet.current &&
      isSaving &&
      isLiveReportPreviewEnabled
    ) {
      dispatch(
        setReportPreviewProps({
          currentData: {
            ...currentForm,
            libraryItems: { ...libraryItems, ...getLibraryItems() },
            tags: null,
            linkedHypotheses: null,
            linkedAnalyses: null,
            lastSelectedKFA: null,
          },
        })
      );
    }
  }, [isSaving, refInitialLivePreviewDataIsSet.current]);

  useEffect(() => {
    if (
      !refInitialLivePreviewDataIsSet.current &&
      isReportBuilderLivePreviewEnabled
    ) {
      refInitialLivePreviewDataIsSet.current = true;

      dispatch(
        setReportPreviewProps({
          contentThreadId: analysis.contentThreadId,
          isShared:
            analysis.versionStatus === Constants.VERSION_STATUS.SHARED.value,
          territoryName: getTandCterritory(),
        })
      );
    }
  }, [refInitialLivePreviewDataIsSet.current]);

  if (isMobile) {
    return (
      <PageNotViewable>
        <Link to={analysesListPath(projectId)}>
          <Button className="full-width-mobile">Back to analysis page</Button>
        </Link>
      </PageNotViewable>
    );
  }

  return (
    <Switch>
      <Route exact path={path}>
        {noDataOnClipboard && (
          <EmptyClipboardModal
            show={noDataOnClipboard}
            onHide={() => setNoDataOnClipboard(false)}
          />
        )}
        <AutoSave
          disableAutoSaveOverride={isCreateForm}
          projectId={projectId}
          contentThreadId={analysis?.contentThreadId}
          isReady={isAutoSaveReady}
          isValid={isFormDataValid}
          initialData={initialForm}
          getData={() => currentForm}
        >
          {({ acquireLockState: { isLoading, isError, isSuccess } }) => {
            if (isError) {
              handleRedirectToView(AUTO_SAVE_LOCK_ERROR);
              return null;
            }

            return (
              <div className="edit-ffa-scroll-component">
                <div id="rich-text-editor" />
                <RTEContextProvider
                  value={{
                    setEditor: setEventListener,
                  }}
                >
                  <form
                    ref={formRef}
                    onSubmit={async (event) => {
                      event.preventDefault();
                      await handlePublish();
                    }}
                  >
                    <div className="form-ffa-wrapper">
                      <FormLayout
                        headerComponent={headerComponent}
                        footerComponent={footerComponent}
                      >
                        <div className="form-content-wrapper">
                          <div>
                            <div
                              className="analysis__contents"
                              data-testid="analysis__contents"
                            >
                              <div
                                className="analysis__contents__details"
                                data-testid="kfa-card-details"
                              >
                                <div className="input-container no-margin">
                                  <TitleFieldsetWithWordCount
                                    field={titleField}
                                    form={{
                                      setFieldValue: (_, newValue) => {
                                        setTitle(newValue);
                                        debouncedSearchTitle(newValue);
                                      },
                                    }}
                                    placeholder="Title (required)"
                                    suggestedLength={35}
                                    hideWordCountWhenInactive
                                    errorMessage={titleError}
                                    dataTestId="text-area-title"
                                  />

                                  <RichTextBalloonEditorFormikField
                                    field={insightField}
                                    form={{
                                      setFieldValue: (_, newValue) => {
                                        setInsight(newValue);
                                      },
                                    }}
                                    label={t('insightOptional')}
                                    dataTestId="text-area-insight"
                                    suggestedLength={420}
                                    hideWordCountWhenInactive
                                    withContainer
                                    containerLabel={t('insight')}
                                    containerIcon="light-bulb"
                                    commentTarget={CommentTarget.Insight}
                                    showCommentIcon
                                  />

                                  <RichTextBalloonEditorFormikField
                                    field={bodyField}
                                    form={{
                                      setFieldValue: (_, newValue) => {
                                        setBody(newValue);
                                      },
                                    }}
                                    label={t('descriptionOptional')}
                                    dataTestId="text-area-body"
                                    suggestedLength={250}
                                    hideWordCountWhenInactive
                                    commentTarget={CommentTarget.Description}
                                    showCommentIcon
                                  />
                                </div>
                              </div>
                              <div className="analysis__contents__analyses-list">
                                <LinkedContentNavigation
                                  projectId={projectId}
                                  connectedAnalyses={
                                    connectedLinkedContent.analyses
                                  }
                                  connectedKfas={
                                    connectedLinkedContent.hypotheses
                                  }
                                  hypothesisIndex={1}
                                  currentContent={analysis}
                                  isCommentingAllowedOnThisItem
                                />
                              </div>
                            </div>
                            <CKEditorContext.Provider
                              value={{
                                enableImageUpload:
                                  featureFlags.isImageUploadEnabled &&
                                  project.ImageIngestionEnabled === 'true',
                              }}
                            >
                              <FreeformAnalysisLayoutContainer
                                goBackUrl={`${url}/library-items`}
                                basePageUrl={url}
                                operationType={analysisId ? 'edit' : 'add'}
                                setLayoutSaved={setLayoutSaved}
                                initialSections={sections}
                                analysisTitle={title}
                                analysisDescription={body}
                                tags={tags}
                              />
                            </CKEditorContext.Provider>
                          </div>

                          <div className="analysis-form-extras">
                            <Separator />

                            <Formik initialValues={{ tags }} enableReinitialize>
                              {() => (
                                <TagEditor
                                  projectId={projectId}
                                  onSelect={handleTagSelect}
                                  isCommentingAllowed
                                />
                              )}
                            </Formik>

                            <EditConnectedContentNew
                              projectId={projectId}
                              parentContentId={parentContentId}
                              setCurrentlyVisibleConnections={setNarratives}
                              allConnections={narratives}
                              connectedContentType="hypotheses"
                              setConnectedContentChanged={
                                setConnectedContentChanged
                              }
                              currentAnalysis={{}}
                              error={error}
                              isCommentingAllowedOnThisItem
                              pageContainerName="edit-ffa-scroll-component"
                            />

                            <EditConnectedContentNew
                              lastItem
                              projectId={projectId}
                              parentContentId={parentContentId}
                              setCurrentlyVisibleConnections={setNarratives}
                              allConnections={narratives}
                              connectedContentType="analyses"
                              setConnectedContentChanged={
                                setConnectedContentChanged
                              }
                              currentAnalysis={analysis}
                              error={error}
                              isCommentingAllowedOnThisItem
                              pageContainerName="edit-ffa-scroll-component"
                            />
                          </div>
                        </div>
                        {errorType === 'publish' && (
                          <>
                            <div className="analysis-form-actions-separator mt-1">
                              <Separator />
                            </div>
                            <D3Alert configType={errorType} />
                          </>
                        )}
                      </FormLayout>
                    </div>
                  </form>
                </RTEContextProvider>
              </div>
            );
          }}
        </AutoSave>
      </Route>
      <Route path={`${path}/library-items`}>
        <LibraryItemListContainer
          title="Add data"
          returnUrl={url}
          operationType={analysisId ? 'edit' : 'add'}
        />
      </Route>
      <Route path={`${path}/layout`}>
        <FreeformAnalysisLayoutContainer
          goBackUrl={`${url}/library-items`}
          basePageUrl={url}
          operationType={analysisId ? 'edit' : 'add'}
          setLayoutSaved={setLayoutSaved}
          initialSections={sections}
          analysisTitle={title}
          analysisInsight={insight}
          analysisDescription={body}
          tags={tags}
        />
      </Route>
    </Switch>
  );
};
export default FreeformAnalysisForm;
